/**
 * <dd-notify-when-available>
 *
 * Subscribes a shopper to Klaviyo's Back-in-Stock flow (and, optionally, to
 * the configured SMS marketing list) for a specific Shopify variant. Talks
 * to the Klaviyo Client API directly — no server-side proxy.
 *
 *   POST https://a.klaviyo.com/client/subscriptions/?company_id={id}           // SMS only
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id={id}
 *   Content-Type: application/vnd.api+json
 *   revision: 2026-04-15
 *
 * Rendered inline inside the sticky ATC footer. The footer's "Notify me"
 * pill is BOTH the open-trigger AND the submit-trigger — on first click it
 * calls `.open()` on this element; on second click it calls `.submit()`.
 * This element no longer renders its own submit button.
 *
 * Events dispatched (all bubble):
 *   - dd-notify:open                 { channel }
 *   - dd-notify:close
 *   - dd-notify:channel-change       { channel }
 *   - dd-notify:submit-start         { channel }
 *   - dd-notify:submit-end           { channel }
 *   - dd-notify:success              { channel, channels, variantId }
 *   - dd-notify:error                { message }
 *   - dd-notify:subscribed-state     { subscribed }
 *
 * Keeps its data-variant-id in sync with PUB_SUB_EVENTS.variantChange.
 */
(function () {
  'use strict';

  if (customElements.get('dd-notify-when-available')) return;

  var KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var KLAVIYO_SUBSCRIPTIONS_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2026-04-15';
  var SESSION_KEY_PREFIX = 'dd:notify:';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function storageKey(variantId) {
    return SESSION_KEY_PREFIX + String(variantId);
  }

  function hasSubscribed(variantId) {
    try {
      return sessionStorage.getItem(storageKey(variantId)) === '1';
    } catch (err) {
      return false;
    }
  }

  function markSubscribed(variantId) {
    try {
      sessionStorage.setItem(storageKey(variantId), '1');
    } catch (err) {
      /* noop */
    }
  }

  function normalizePhoneToE164US(raw) {
    if (!raw) return null;
    var digits = String(raw).replace(/\D+/g, '');
    if (!digits) return null;
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    if (digits.length >= 11 && digits.length <= 15 && String(raw).trim().charAt(0) === '+') {
      return '+' + digits;
    }
    return null;
  }

  function catalogVariantId(shopifyVariantId) {
    return '$shopify:::$default:::' + String(shopifyVariantId);
  }

  class DdNotifyWhenAvailable extends HTMLElement {
    constructor() {
      super();
      this._variantUnsub = null;
      this._inFlight = false;
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-dd-notify-panel]');
      this.closeBtn = this.querySelector('[data-dd-notify-close]');
      this.channelInputs = this.querySelectorAll('[data-dd-notify-channel]');
      this.emailPane = this.querySelector('[data-dd-notify-pane="email"]');
      this.smsPane = this.querySelector('[data-dd-notify-pane="sms"]');
      this.emailInput = this.querySelector('[data-dd-notify-email]');
      this.emailError = this.querySelector('[data-dd-notify-email-error]');
      this.phoneInput = this.querySelector('[data-dd-notify-phone]');
      this.phoneError = this.querySelector('[data-dd-notify-phone-error]');
      this.tcpaInput = this.querySelector('[data-dd-notify-tcpa]');
      this.tcpaWrap = this.querySelector('.dd-notify__tcpa');
      this.tcpaToast = this.querySelector('[data-dd-notify-tcpa-toast]');
      this.tcpaToastText = this.querySelector('[data-dd-notify-tcpa-toast-text]');
      this.successBox = this.querySelector('[data-dd-notify-success]');
      this.successText = this.querySelector('[data-dd-notify-success-text]');
      this.errorBox = this.querySelector('[data-dd-notify-error]');
      this.errorText = this.querySelector('[data-dd-notify-error-text]');

      if (!this.panel) return;

      this._onSubmit = this._onSubmit.bind(this);
      this._onChannelChange = this._onChannelChange.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onTcpaInputChange = this._onTcpaInputChange.bind(this);

      if (this.closeBtn) this.closeBtn.addEventListener('click', this._onCloseClick);
      if (this.channelInputs && this.channelInputs.length) {
        var self = this;
        Array.prototype.forEach.call(this.channelInputs, function (input) {
          input.addEventListener('change', self._onChannelChange);
        });
      }
      if (this.tcpaInput) this.tcpaInput.addEventListener('change', this._onTcpaInputChange);
      this.addEventListener('keydown', this._onKeydown);

      this._syncDedupeState();
      this._syncChannelPanes();
      this.syncRestockEta();

      // PUB_SUB variantChange is a PDP singleton bus. Card-modal (and any
      // future imperative-only contexts) must NOT subscribe — they are
      // driven exclusively via applyVariant().
      var ctx = (this.dataset.context || '').trim();
      var usesPubSub =
        ctx === 'pdp' ||
        ctx === 'pdp-inline' ||
        ctx === 'sticky-inline' ||
        ctx === '';
      if (
        usesPubSub &&
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange);
      }
    }

    disconnectedCallback() {
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this._onCloseClick);
      if (this.channelInputs && this.channelInputs.length) {
        var self = this;
        Array.prototype.forEach.call(this.channelInputs, function (input) {
          input.removeEventListener('change', self._onChannelChange);
        });
      }
      if (this.tcpaInput) this.tcpaInput.removeEventListener('change', this._onTcpaInputChange);
      this.removeEventListener('keydown', this._onKeydown);
      if (typeof this._variantUnsub === 'function') {
        this._variantUnsub();
        this._variantUnsub = null;
      }
    }

    // --- Public state helpers -------------------------------------------------

    get variantId() {
      return this.dataset.variantId || '';
    }

    set variantId(val) {
      this.dataset.variantId = String(val || '');
    }

    get inventoryPolicy() {
      return this.dataset.inventoryPolicy || '';
    }

    get allowSms() {
      return this.dataset.allowSms === 'true';
    }

    get companyId() {
      return (this.dataset.companyId || '').trim();
    }

    get smsListId() {
      return (this.dataset.smsListId || '').trim();
    }

    get isOpen() {
      return !!(this.panel && !this.panel.hidden);
    }

    get selectedChannel() {
      return this._selectedChannel();
    }

    get isSubscribed() {
      return this.classList.contains('is-subscribed');
    }

    // --- Public API (called by dd-sticky-footer.js) ---------------------------

    open() {
      if (!this.panel) return;
      if (this.isOpen) return;
      this.panel.hidden = false;
      this.classList.add('is-expanded');
      this._focusFirstActionable();
      this.dispatchEvent(
        new CustomEvent('dd-notify:open', {
          bubbles: true,
          detail: { channel: this._selectedChannel() },
        })
      );
    }

    close() {
      if (!this.panel) return;
      if (!this.isOpen) return;
      this.panel.hidden = true;
      this.classList.remove('is-expanded');
      this._hideTcpaError();
      this.dispatchEvent(new CustomEvent('dd-notify:close', { bubbles: true }));
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    // Public submit — invoked by the sticky-footer trigger on its second click.
    submit() {
      return this._onSubmit();
    }

    /**
     * Imperative retarget for non-PDP hosts (card-modal).
     * Payload: { id, available, inventory_policy, title, productId?, productTitle? }
     * Returns false when ignored (in-flight submit) or when the variant
     * does not need notify (in-stock / continue).
     */
    applyVariant(variant) {
      if (this._inFlight) return false;
      if (!variant || variant.id == null) {
        this.hidden = true;
        this.close();
        return false;
      }

      var isAvailable = variant.available === true;
      var isContinue = variant.inventory_policy === 'continue';
      var needsNotify = !isAvailable && !isContinue;

      if (!needsNotify) {
        this.hidden = true;
        this.close();
        return false;
      }

      var newId = String(variant.id);
      var sameId = newId === String(this.variantId);

      this.hidden = false;
      this.variantId = newId;
      this.dataset.variantTitle = variant.title || '';
      this.dataset.inventoryPolicy = variant.inventory_policy || '';
      if (variant.productId != null) {
        this.dataset.productId = String(variant.productId);
      }
      if (variant.productTitle != null) {
        this.dataset.productTitle = String(variant.productTitle);
      }

      if (!sameId) {
        this._hideStatuses();
        this.close();
        this._syncDedupeState();
      } else {
        this._syncDedupeState();
      }
      this.syncRestockEta();
      return true;
    }

    syncRestockEta() {
      var el = this.querySelector('[data-dd-notify-restock]');
      if (!el) return;
      var label = typeof window.getRestockLabel === 'function'
        ? window.getRestockLabel(this.variantId)
        : '';
      var show = !!(label && !(typeof window.ddIsRestockFallback === 'function'
        ? window.ddIsRestockFallback(label)
        : label === 'Sold out' || label === 'Awaiting Restock'));
      el.textContent = show ? label : '';
      el.hidden = !show;
    }

    // --- Event handlers -------------------------------------------------------

    _onCloseClick() {
      this.close();
    }

    _onChannelChange() {
      this._syncChannelPanes();
      this._clearFieldError('phone');
      this._clearFieldError('email');
      this._hideTcpaError();
      this.dispatchEvent(
        new CustomEvent('dd-notify:channel-change', {
          bubbles: true,
          detail: { channel: this._selectedChannel() },
        })
      );
    }

    _onTcpaInputChange() {
      /* Auto-clear the inline consent toast as soon as the shopper
         actually ticks the box — no second submit attempt required to
         drop the warning. We don't show one on uncheck either; the
         backlit pulse already handles that ambient cue. */
      if (this.tcpaInput && this.tcpaInput.checked) {
        this._hideTcpaError();
      }
    }

    _syncChannelPanes() {
      var selected = this._selectedChannel();
      if (this.emailPane) this.emailPane.hidden = selected !== 'email';
      if (this.smsPane) this.smsPane.hidden = selected !== 'sms';
    }

    _selectedChannel() {
      var channel = 'email';
      if (this.channelInputs && this.channelInputs.length) {
        Array.prototype.forEach.call(this.channelInputs, function (input) {
          if (input.checked) channel = input.value || 'email';
        });
      }
      return channel;
    }

    _onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Enter') {
        var t = e.target;
        if (!t) return;
        // Allow Enter to submit when focus is on a text input (not on a
        // radio/checkbox or the close button).
        if (t.tagName === 'INPUT' && t.type !== 'radio' && t.type !== 'checkbox') {
          e.preventDefault();
          this.submit();
        }
      }
    }

    _focusFirstActionable() {
      // Land focus on a visible input if there is one; otherwise the close
      // button so the shopper can back out without a mouse.
      var visibleInput = null;
      var selected = this._selectedChannel();
      if (selected === 'email' && this.emailInput && this.emailInput.type === 'email') {
        visibleInput = this.emailInput;
      } else if (selected === 'sms' && this.phoneInput && this.phoneInput.type === 'tel') {
        visibleInput = this.phoneInput;
      }
      var target = visibleInput || this.closeBtn;
      try { target && target.focus({ preventScroll: true }); } catch (_) { target && target.focus(); }
    }

    _onVariantChange(event) {
      var variant = event && event.data ? event.data.variant : null;

      if (!variant) {
        this.hidden = true;
        this.close();
        return;
      }

      var isAvailable = variant.available === true;
      var isContinue = variant.inventory_policy === 'continue';
      var needsNotify = !isAvailable && !isContinue;

      if (!needsNotify) {
        this.hidden = true;
        this.close();
        return;
      }
      this.hidden = false;

      var newId = String(variant.id);
      if (newId === String(this.variantId)) {
        this.syncRestockEta();
        return;
      }

      this.variantId = newId;
      this.dataset.variantTitle = variant.title || '';
      this.dataset.inventoryPolicy = variant.inventory_policy || '';

      this._hideStatuses();
      this.close();
      this._syncDedupeState();
      this.syncRestockEta();
    }

    // --- UI state -------------------------------------------------------------

    _syncDedupeState() {
      if (!this.variantId) return;
      if (hasSubscribed(this.variantId)) {
        this._showAlreadySubscribed();
      } else {
        this._resetToDefault();
      }
    }

    _showAlreadySubscribed() {
      this._hideStatuses();
      if (this.successText) {
        this.successText.textContent = "You're already on the list for this option. We'll let you know the moment it's back.";
      }
      if (this.successBox) this.successBox.hidden = false;
      if (this.emailInput) this.emailInput.disabled = true;
      if (this.phoneInput) this.phoneInput.disabled = true;
      if (this.tcpaInput) this.tcpaInput.disabled = true;
      if (this.channelInputs && this.channelInputs.length) {
        Array.prototype.forEach.call(this.channelInputs, function (input) { input.disabled = true; });
      }
      this.classList.add('is-subscribed');
      this.dispatchEvent(new CustomEvent('dd-notify:subscribed-state', { bubbles: true, detail: { subscribed: true } }));
    }

    _resetToDefault() {
      if (this.emailInput) this.emailInput.disabled = false;
      if (this.phoneInput) this.phoneInput.disabled = false;
      if (this.tcpaInput) this.tcpaInput.disabled = false;
      if (this.channelInputs && this.channelInputs.length) {
        Array.prototype.forEach.call(this.channelInputs, function (input) { input.disabled = false; });
      }
      this.classList.remove('is-subscribed');
      if (this.successText) {
        this.successText.textContent = "You're on the list — we'll let you know the moment it's back.";
      }
      this.dispatchEvent(new CustomEvent('dd-notify:subscribed-state', { bubbles: true, detail: { subscribed: false } }));
    }

    _hideStatuses() {
      if (this.successBox) this.successBox.hidden = true;
      if (this.errorBox) this.errorBox.hidden = true;
    }

    _setFieldError(field, message) {
      var el = field === 'email' ? this.emailError : this.phoneError;
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    _clearFieldError(field) {
      this._setFieldError(field, '');
    }

    _showTcpaError(message) {
      /*
        Surfaces the "must agree to set up the notification" hint as an
        inline toast directly above the TCPA box AND triggers a one-shot
        shake on the consent label so the requirement is unmistakable.

        Why a separate channel from `_setFieldError`?
          - Field errors are meant to live with their input; the TCPA
            toast belongs visually to the consent label below it, not
            to the phone field.
          - Re-showing on a repeat submit must replay the shake
            animation. We do that by removing `.is--shake`, forcing a
            reflow, then re-adding it — otherwise the browser reuses
            the prior animation run and the user sees nothing on the
            second invalid submit.
          - The backlit pulse animation on `.dd-notify__tcpa` is on
            `animation`. The shake class uses the same property, so it
            replaces the pulse for the shake duration and the pulse
            comes back automatically once the class is removed.
      */
      if (this.tcpaToast) {
        if (this.tcpaToastText && message) this.tcpaToastText.textContent = message;
        this.tcpaToast.hidden = false;
      }
      var wrap = this.tcpaWrap;
      if (wrap) {
        wrap.classList.remove('is--shake');
        /* Force layout so the next add() restarts the keyframes. */
        // eslint-disable-next-line no-unused-expressions
        wrap.offsetWidth;
        wrap.classList.add('is--shake');

        if (this._tcpaShakeTimer) {
          clearTimeout(this._tcpaShakeTimer);
        }
        /* Slightly longer than the 820ms keyframes so the class is
           guaranteed to outlive the animation; otherwise the backlit
           pulse can pop back in mid-shake on slower frames. */
        this._tcpaShakeTimer = setTimeout(function () {
          wrap.classList.remove('is--shake');
        }, 950);
      }
    }

    _hideTcpaError() {
      if (this.tcpaToast) this.tcpaToast.hidden = true;
      if (this.tcpaWrap) this.tcpaWrap.classList.remove('is--shake');
      if (this._tcpaShakeTimer) {
        clearTimeout(this._tcpaShakeTimer);
        this._tcpaShakeTimer = null;
      }
    }

    _showError(message) {
      if (this.errorBox) {
        if (this.errorText) this.errorText.textContent = message || 'Something went wrong. Please try again.';
        this.errorBox.hidden = false;
      }
      this.dispatchEvent(
        new CustomEvent('dd-notify:error', {
          bubbles: true,
          detail: { message: message || '' },
        })
      );
    }

    _setInFlight(flag) {
      this._inFlight = !!flag;
      this.dispatchEvent(
        new CustomEvent(flag ? 'dd-notify:submit-start' : 'dd-notify:submit-end', {
          bubbles: true,
          detail: { channel: this._selectedChannel() },
        })
      );
    }

    // --- Submit ---------------------------------------------------------------

    _validate() {
      this._clearFieldError('email');
      this._clearFieldError('phone');
      this._hideTcpaError();
      this._hideStatuses();

      var channel = this._selectedChannel();
      var email = (this.emailInput && this.emailInput.value || '').trim();
      var phoneE164 = null;
      var channels = [];

      // Email is always used for profile identity when available (prefill).
      // It is *required* in 'email' channel mode, and *optional* in 'sms' mode.
      if (channel === 'email') {
        if (!email || !EMAIL_RE.test(email)) {
          this._setFieldError('email', 'Please enter a valid email address.');
          if (this.emailInput && this.emailInput.type === 'email') this.emailInput.focus();
          return null;
        }
        channels.push('EMAIL');
      } else if (channel === 'sms') {
        if (!this.tcpaInput || !this.tcpaInput.checked) {
          this._showTcpaError('Please agree to the SMS terms to set up your notification.');
          if (this.tcpaInput) {
            try { this.tcpaInput.focus({ preventScroll: true }); }
            catch (_) { this.tcpaInput.focus(); }
          }
          return null;
        }
        phoneE164 = normalizePhoneToE164US(this.phoneInput && this.phoneInput.value);
        if (!phoneE164) {
          this._setFieldError('phone', 'Please enter a valid US mobile number.');
          if (this.phoneInput && this.phoneInput.type === 'tel') this.phoneInput.focus();
          return null;
        }
        channels.push('SMS');
      }

      return {
        email: email || null,
        phone: phoneE164,
        channels: channels,
        channelMode: channel,
      };
    }

    async _klaviyoPost(url, body) {
      // Klaviyo's client endpoints require Content-Type: application/json
      // (NOT application/vnd.api+json). Using the vendor type triggers a
      // CORS preflight that Klaviyo rejects and the request never lands.
      // See:
      //   https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock
      //   https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });
    }

    async _subscribeSms(companyId, listId, email, phoneE164) {
      var profileAttrs = { phone_number: phoneE164 };
      if (email) profileAttrs.email = email;
      profileAttrs.subscriptions = { sms: { marketing: { consent: 'SUBSCRIBED' } } };

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in Stock',
            profile: { data: { type: 'profile', attributes: profileAttrs } },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      };
      var url = KLAVIYO_SUBSCRIPTIONS_ENDPOINT + '?company_id=' + encodeURIComponent(companyId);
      return this._klaviyoPost(url, body);
    }

    async _subscribeBis(companyId, variantId, payload) {
      var profileAttrs = {};
      if (payload.email) profileAttrs.email = payload.email;
      if (payload.phone) profileAttrs.phone_number = payload.phone;

      var body = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: payload.channels,
            profile: { data: { type: 'profile', attributes: profileAttrs } },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: catalogVariantId(variantId),
              },
            },
          },
        },
      };
      var url = KLAVIYO_BIS_ENDPOINT + '?company_id=' + encodeURIComponent(companyId);
      return this._klaviyoPost(url, body);
    }

    async _readErrorBody(res) {
      try { return await res.json(); } catch (_) { return null; }
    }

    async _onSubmit(e) {
      if (e) e.preventDefault();
      if (this._inFlight) return;

      var companyId = this.companyId;
      if (!companyId) {
        this._showError('Restock alerts are temporarily unavailable.');
        if (window.console) console.warn('[dd-notify] missing companyId — check settings.dd_notify_company_id');
        return;
      }

      var variantId = this.variantId;
      if (!variantId) {
        this._showError('Please pick an option first.');
        if (window.console) console.warn('[dd-notify] missing variantId');
        return;
      }

      var payload = this._validate();
      if (!payload) {
        if (window.console) console.info('[dd-notify] validation failed — check field errors in the form');
        return;
      }

      var smsOn = payload.channelMode === 'sms';
      var listId = this.smsListId;
      if (smsOn && !listId) {
        // Mis-config — rather than 400ing, fall back to email-only if we can.
        if (payload.email) {
          payload.channelMode = 'email';
          payload.channels = ['EMAIL'];
          payload.phone = null;
          smsOn = false;
        } else {
          this._showError('Text alerts are temporarily unavailable.');
          return;
        }
      }

      this._setInFlight(true);

      if (window.console) {
        console.info('[dd-notify] submit', {
          variantId: variantId,
          catalogVariantId: catalogVariantId(variantId),
          channelMode: payload.channelMode,
          channels: payload.channels,
          hasEmail: !!payload.email,
          hasPhone: !!payload.phone,
          companyId: companyId,
          smsListId: listId || null,
        });
      }

      try {
        if (smsOn) {
          var subRes = await this._subscribeSms(companyId, listId, payload.email, payload.phone);

          if (window.console) console.info('[dd-notify] SMS consent response', subRes.status);

          if (subRes.status === 429) {
            this._showError('Too many attempts right now. Please try again in a moment.');
            return;
          }

          if (subRes.status !== 202) {
            var subErr = await this._readErrorBody(subRes);
            if (window.console) console.warn('[dd-notify] SMS consent failed', subRes.status, subErr);
            this._showError(this._friendlyConsentError(subRes.status, subErr));
            return;
          }
        }

        var res = await this._subscribeBis(companyId, variantId, payload);

        if (window.console) console.info('[dd-notify] BIS response', res.status);

        if (res.status === 202) {
          markSubscribed(variantId);
          this._handleSuccess(payload);
          return;
        }

        if (res.status === 429) {
          this._showError('Too many attempts right now. Please try again in a moment.');
          return;
        }

        var errBody = await this._readErrorBody(res);
        if (window.console) console.warn('[dd-notify] Klaviyo BIS error', res.status, errBody);
        this._showError("We couldn't subscribe you just now. Please try again.");
      } catch (err) {
        if (window.console) console.warn('[dd-notify] network error', err);
        this._showError('Network error. Please check your connection and try again.');
      } finally {
        this._setInFlight(false);
      }
    }

    _friendlyConsentError(status, body) {
      var detail = '';
      try {
        if (body && body.errors && body.errors.length) {
          detail = (body.errors[0].detail || body.errors[0].title || '').toLowerCase();
        }
      } catch (_) { /* noop */ }

      if (status === 400 && detail.indexOf('phone') !== -1) {
        return 'That phone number doesn\u2019t look right. Please double-check and try again.';
      }
      if (status === 400 && detail.indexOf('sms configuration') !== -1) {
        return 'Text alerts are temporarily unavailable. Try email instead.';
      }
      return "We couldn't sign you up for text alerts. Try email instead.";
    }

    _handleSuccess(payload) {
      this._hideStatuses();
      if (this.successText) {
        this.successText.textContent =
          payload.channelMode === 'sms'
            ? "You're on the list — we'll text you the moment it's back."
            : "You're on the list — we'll let you know the moment it's back.";
      }
      if (this.successBox) this.successBox.hidden = false;
      if (this.emailInput) this.emailInput.disabled = true;
      if (this.phoneInput) this.phoneInput.disabled = true;
      if (this.tcpaInput) this.tcpaInput.disabled = true;
      if (this.channelInputs && this.channelInputs.length) {
        Array.prototype.forEach.call(this.channelInputs, function (input) { input.disabled = true; });
      }
      this.classList.add('is-subscribed');

      this.dispatchEvent(
        new CustomEvent('dd-notify:success', {
          bubbles: true,
          detail: {
            channel: payload.channelMode,
            channels: payload.channels,
            variantId: String(this.variantId),
          },
        })
      );

      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'back_in_stock_subscribed',
          variant_id: String(this.variantId),
          product_id: String(this.dataset.productId || ''),
          channels: payload.channels,
        });
      } catch (err) {
        /* noop */
      }
    }
  }

  customElements.define('dd-notify-when-available', DdNotifyWhenAvailable);
})();
